import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import api from "@/api";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency: "EUR",
  }).format(value || 0);
};

const formatDate = (date) => {
  if (!date) return "";
  return dayjs(date).format("DD.MM.YYYY");
};

const getLogoBase64 = async () => {
  try {
    const response = await fetch("/img/icons/android-chrome-512x512.png");
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.error("Could not load logo", e);
    return null;
  }
};

async function fetchImageAsBase64(url) {
  if (!url) return null;
  if (url.startsWith("data:")) return url;

  try {
    // Try fetching directly first (works for same-origin or CORS-enabled)
    const response = await fetch(url);
    if (!response.ok) throw new Error("Network response was not ok");
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.log("Direct fetch failed, trying backend proxy...", e);
    try {
      const result = await api.fetchImage({ url });
      return `data:${result.data.mimeType};base64,${result.data.base64}`;
    } catch (backendError) {
      console.error("Backend fetch failed", backendError);
      return null;
    }
  }
}

const calculateTotals = (claim) => {
  const dailyAllowance =
    (claim.dailyAllowance?.fullDayTotal || 0) +
    (claim.dailyAllowance?.partialDayTotal || 0);

  const mileage = (claim.mileage || []).reduce(
    (sum, item) => sum + (item.distanceKm || 0) * (item.unitPrice || 0),
    0,
  );

  const receipts = (claim.receipts || []).reduce((sum, receipt) => {
    const receiptTotal = receipt.lines
      ? receipt.lines.reduce((lSum, line) => lSum + (line.amount || 0), 0)
      : receipt.amount || 0;
    return sum + receiptTotal;
  }, 0);

  return {
    dailyAllowance,
    mileage,
    receipts,
    grandTotal: dailyAllowance + mileage + receipts,
  };
};

export async function generateClaimPDF(claimData) {
  const claim = { ...claimData };
  if (!claim.totals) {
    claim.totals = calculateTotals(claim);
  }

  const doc = new jsPDF();
  const logoBase64 = await getLogoBase64();

  // 1. Header
  if (logoBase64) {
    doc.addImage(logoBase64, "PNG", 15, 10, 25, 25);
  }

  // Company Info
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Noormarkun VPK ry", 195, 15, { align: "right" });
  doc.text("Palotie 4", 195, 20, { align: "right" });
  doc.text("29600 Noormarkku", 195, 25, { align: "right" });

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  const currentYear = new Date().getFullYear();
  const title =
    claim.type === "expense"
      ? `KULULASKU ${currentYear}`
      : `MATKA/KULULASKU ${currentYear}`;
  doc.text(title, 105, 25, { align: "center" });

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Saaja: ${claim.memberName}`, 15, 45);
  doc.text(`IBAN: ${claim.iban}`, 15, 50);

  // 2. Cost Allocation
  doc.text("Kustannusten kohdistus:", 15, 60);
  const allocationOptions = [
    { key: "halytysosasto", label: "Hälytysosasto" },
    { key: "nuorisoOsasto", label: "Nuoriso-osasto" },
    { key: "varhaisnuorisoOsasto", label: "Varhaisnuoriso-osasto" },
    { key: "naisosasto", label: "Tukiosasto" },
    { key: "veteraaniOsasto", label: "Veteraaniosasto" },
    { key: "yhdistystoiminta", label: "Yhdistystoiminta" },
    { key: "leirikoski", label: "Leirikoski" },
  ];

  let allocX = 15;
  let allocY = 65;
  allocationOptions.forEach((opt, index) => {
    if (index > 0 && index % 2 === 0) {
      allocX = 15;
      allocY += 6;
    } else if (index > 0) {
      allocX = 100;
    }

    doc.rect(allocX, allocY - 3, 4, 4);
    if (claim.costAllocation && claim.costAllocation[opt.key]) {
      doc.text("X", allocX + 1, allocY);
    }
    doc.text(opt.label, allocX + 6, allocY);
  });

  // 3. Trip Details
  let currentY = allocY + 15;

  if (claim.type === "travel") {
    const tripReasonLines = doc
      .splitTextToSize(
        `Matkan syy / Tarkoitus: ${claim.tripReason || "-"}`,
        180,
      )
      .slice(0, 3);
    doc.text(tripReasonLines, 15, currentY);

    currentY += 7 + (tripReasonLines.length - 1) * 5;
    const start = `${formatDate(claim.dailyAllowance.startDate)} ${
      claim.dailyAllowance.startTime || ""
    }`;
    const end = `${formatDate(claim.dailyAllowance.endDate)} ${
      claim.dailyAllowance.endTime || ""
    }`;
    doc.text(`Matka alkoi: ${start}`, 15, currentY);
    doc.text(`Matka päättyi: ${end}`, 100, currentY);

    currentY += 7;
    const fullDayRate = claim.dailyAllowance.fullDayRate || 54;
    const freeMealFullDays = claim.dailyAllowance.freeMealFullDays || 0;
    const fullDaysNormal =
      (claim.dailyAllowance.fullDayCount || 0) - freeMealFullDays;

    let fullDayText = `Kokopäiväraha (yli 10h): ${fullDaysNormal} kpl x ${formatCurrency(
      fullDayRate,
    )}`;
    if (freeMealFullDays > 0) {
      fullDayText += ` + ${freeMealFullDays} kpl x ${formatCurrency(
        fullDayRate / 2,
      )} (ilmainen ruoka)`;
    }
    fullDayText += ` = ${formatCurrency(claim.dailyAllowance.fullDayTotal)}`;
    doc.text(fullDayText, 15, currentY);

    currentY += 7;
    const partialDayRate = claim.dailyAllowance.partialDayRate || 25;
    const freeMealPartialDays = claim.dailyAllowance.freeMealPartialDays || 0;
    const partialDaysNormal =
      (claim.dailyAllowance.partialDayCount || 0) - freeMealPartialDays;

    let partialDayText = `Osapäiväraha (yli 6h): ${partialDaysNormal} kpl x ${formatCurrency(
      partialDayRate,
    )}`;
    if (freeMealPartialDays > 0) {
      partialDayText += ` + ${freeMealPartialDays} kpl x ${formatCurrency(
        partialDayRate / 2,
      )} (ilmainen ruoka)`;
    }
    partialDayText += ` = ${formatCurrency(
      claim.dailyAllowance.partialDayTotal,
    )}`;
    doc.text(partialDayText, 15, currentY);

    // 4. Mileage Table
    currentY += 10;
    doc.text("Kilometrikorvaukset", 15, currentY);

    const mileageRates = [
      { label: "Oma auto", value: "car", price: 0.55 },
      { label: "Perävaunu", value: "trailer", price: 0.09 },
      { label: "Matkatavara", value: "heavy", price: 0.04 },
      { label: "Matkustaja", value: "passenger", price: 0.04 },
    ];

    const mileageBody = (claim.mileage || []).map((m) => {
      // Resolve rate descriptions
      let rateDesc = "";
      if (m.selectedRateValues && m.selectedRateValues.length > 0) {
        const parts = m.selectedRateValues.map((val) => {
          const r = mileageRates.find((mr) => mr.value === val);
          if (r) return `${r.label} (${formatCurrency(r.price)})`;
          if (val.startsWith("custom_"))
            return `Muu (${formatCurrency(parseFloat(val.split("_")[1]))})`;
          return val;
        });
        rateDesc = parts.join("\n");
      } else {
        rateDesc = formatCurrency(m.unitPrice);
      }

      let passengerInfo = m.passengers ? `${m.passengers} kpl` : "";
      if (m.passengerNames) {
        passengerInfo += `\n(${m.passengerNames})`;
      }

      return [
        formatDate(m.date),
        m.route,
        passengerInfo,
        m.distanceKm,
        rateDesc,
        formatCurrency(m.distanceKm * m.unitPrice),
      ];
    });

    autoTable(doc, {
      startY: currentY + 2,
      head: [["Pvm", "Reitti", "Matkustajat", "Km", "Yksikköhinta", "Yht"]],
      body: mileageBody,
      foot: [
        ["", "", "Yhteensä", "", "", formatCurrency(claim.totals.mileage)],
      ],
      theme: "grid",
      headStyles: { fillColor: [220, 220, 220], textColor: 0 },
      footStyles: { fillColor: [108, 0, 0], textColor: 255 },
    });

    currentY = doc.lastAutoTable.finalY + 10;
  } else {
    // Expense claim only - just show description if available
    const tripReasonLines = doc
      .splitTextToSize(
        `Matkan syy / Tarkoitus: ${claim.tripReason || "-"}`,
        180,
      )
      .slice(0, 3);
    doc.text(tripReasonLines, 15, currentY);
    currentY += 7 + (tripReasonLines.length - 1) * 5;

    if (claim.additionalInfo) {
      doc.text(`Lisätiedot: ${claim.additionalInfo}`, 15, currentY);
      currentY += 10;
    }
  }

  // 5. Receipts Table
  doc.text("Muut kulut / Tositteet", 15, currentY);

  const receiptsBody = [];
  (claim.receipts || []).forEach((r) => {
    if (r.lines && r.lines.length > 0) {
      r.lines.forEach((line, index) => {
        const quantity = line.quantity ? `${line.quantity} kpl` : "";
        const unitPrice = line.unitPrice ? formatCurrency(line.unitPrice) : "";

        receiptsBody.push([
          index === 0 ? formatDate(r.date) : "",
          line.description,
          quantity,
          unitPrice,
          formatCurrency(line.amount),
        ]);
      });
    } else {
      // Fallback for old data
      receiptsBody.push([
        formatDate(r.date),
        r.description,
        "",
        "",
        formatCurrency(r.amount),
      ]);
    }
  });

  autoTable(doc, {
    startY: currentY + 2,
    head: [["Pvm", "Kuvaus", "Määrä", "á-hinta", "Summa"]],
    body: receiptsBody,
    foot: [["", "Yhteensä", "", "", formatCurrency(claim.totals.receipts)]],
    theme: "grid",
    headStyles: { fillColor: [220, 220, 220], textColor: 0 },
    footStyles: { fillColor: [108, 0, 0], textColor: 255 },
  });

  currentY = doc.lastAutoTable.finalY + 15;

  // 6. Summary & Totals
  const summaryX = 120;
  if (claim.type === "travel") {
    doc.text(
      `Päivärahat yht: ${formatCurrency(claim.totals.dailyAllowance)}`,
      summaryX,
      currentY,
    );
    currentY += 7;
    doc.text(
      `Kilometrit yht: ${formatCurrency(claim.totals.mileage)}`,
      summaryX,
      currentY,
    );
    currentY += 7;
  }

  doc.text(
    `Muut kulut yht: ${formatCurrency(claim.totals.receipts)}`,
    summaryX,
    currentY,
  );
  currentY += 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(
    `MAKSETTAVA YHTEENSÄ: ${formatCurrency(claim.totals.grandTotal)}`,
    summaryX,
    currentY,
  );

  // 7. Signatures
  // Check if we need a new page for signatures
  if (currentY + 80 > doc.internal.pageSize.getHeight()) {
    doc.addPage();
    currentY = 20;
  } else {
    currentY += 20;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(
    "Allekirjoituksellani todistan matkalaskun tiedot oikeiksi.",
    15,
    currentY,
  );

  currentY += 15;
  doc.text(
    `Aika ja paikka: ${formatDate(claim.submissionDate)} Noormarkku`,
    15,
    currentY,
  );

  // Add Signature Image if exists
  if (claim.signatureUrl) {
    try {
      const sigData = await fetchImageAsBase64(claim.signatureUrl);
      if (sigData) {
        doc.addImage(sigData, "PNG", 15, currentY + 2, 50, 25);
      }
    } catch (e) {
      console.error("Failed to add signature to PDF", e);
    }
  }

  currentY += 30;
  doc.line(15, currentY, 80, currentY);
  doc.text("Allekirjoitus", 15, currentY + 5);

  doc.line(100, currentY, 165, currentY);
  doc.text("Hyväksyjä", 100, currentY + 5);

  // 8. Attachments (Receipt Images)
  if (claim.receipts && claim.receipts.length > 0) {
    for (const receipt of claim.receipts) {
      if (receipt.imageUrl) {
        // Assuming imageUrl is the base64 or url
        doc.addPage();

        const total = receipt.lines
          ? receipt.lines.reduce((s, l) => s + (l.amount || 0), 0)
          : receipt.amount || 0;
        const desc = receipt.lines
          ? receipt.lines.map((l) => l.description).join(", ")
          : receipt.description;

        doc.text(
          `Liite: ${formatDate(receipt.date)} - ${formatCurrency(total)}`,
          15,
          20,
        );

        // Wrap long description
        const splitDesc = doc.splitTextToSize(desc, 180);
        doc.setFontSize(8);
        doc.text(splitDesc, 15, 25);
        doc.setFontSize(10);

        try {
          // If it's a URL (Firebase Storage)
          // We need to fetch it and convert to base64 or use addImage with URL if supported (and CORS allows)
          // jsPDF addImage supports URL but it's better to fetch blob to be safe with CORS if configured
          // But if we use the FileUpload component's output (base64), it works directly.

          const base64data = await fetchImageAsBase64(receipt.imageUrl);

          if (base64data) {
            const props = doc.getImageProperties(base64data);
            const maxWidth = doc.internal.pageSize.getWidth() - 30;
            const maxHeight = doc.internal.pageSize.getHeight() - 35 - 15;
            let pdfWidth = maxWidth;
            let pdfHeight = (props.height * pdfWidth) / props.width;

            if (pdfHeight > maxHeight) {
              pdfHeight = maxHeight;
              pdfWidth = (props.width * pdfHeight) / props.height;
            }

            doc.addImage(base64data, "JPEG", 15, 35, pdfWidth, pdfHeight);
          } else {
            doc.text("Kuvan lataaminen epäonnistui (URL)", 15, 40);
          }
        } catch (e) {
          console.error("Failed to add receipt image", e);
          doc.text("Kuvan lataaminen epäonnistui", 15, 40);
        }
      }
    }
  }

  return doc;
}
